<template>
  <div :class="$options.name">
    <div class="upload-field__list">
      <div v-for="(img, i) in allImages"
        :key="img.key"
        :class="['upload-field__preview', { 'upload-field__preview--grid': i === 0 && img.uploaded }]">
        <img :src="img.preview"
          alt="" />
        <span v-if="i === 0 && img.uploaded"
          class="upload-field__grid-badge"
          title="Põhipilt">
          <i class="fa-solid fa-star" />
        </span>
        <button v-if="i > 0 && img.uploaded"
          type="button"
          class="upload-field__set-grid"
          title="Määra põhipildiks"
          @click="setGridImage(i)">
          <i class="fa-solid fa-star" />
        </button>
        <button v-if="img.uploaded && colorOptions.length"
          type="button"
          :class="['upload-field__color-btn', { 'upload-field__color-btn--active': img.color }]"
          :title="img.color ? 'Värv: ' + img.color : 'Lisa värv'"
          @click="toggleColorEdit(i)">
          <i class="fa-solid fa-palette" />
        </button>
        <button type="button"
          class="upload-field__remove"
          @click="remove(i)">
          <i class="fa-solid fa-xmark" />
        </button>
        <span v-if="img.color"
          class="upload-field__color-label">{{ img.color }}</span>
        <div v-if="colorEditIndex === i"
          class="upload-field__color-edit">
          <select v-model="colorEditValue"
            class="upload-field__color-select"
            @change="saveColor(i)">
            <option value="">— Vali värv —</option>
            <option v-for="opt in colorOptions"
              :key="opt"
              :value="opt">{{ opt }}</option>
          </select>
          <button v-if="img.color"
            type="button"
            class="btn btn--sm btn-danger"
            @click="removeColor(i)">
            <i class="fa-solid fa-xmark" />
          </button>
        </div>
      </div>
      <label class="upload-field__dropzone"
        :class="{ uploading }">
        <i class="fa-solid fa-cloud-arrow-up" />
        <span>{{ uploading ? 'Laadin...' : 'Lisa pilt' }}</span>
        <input type="file"
          accept="image/*"
          multiple
          @change="addFiles"
          :disabled="uploading" />
      </label>
    </div>
    <Field v-model="imageUrl"
      class="upload-field__url"
      type="url"
      size="sm"
      placeholder="Pildi URL (https://...)"
      :disabled="fetchingUrl"
      @keydown.enter.prevent="addFromUrl">
      <template #action>
        <button type="button"
          class="field__action"
          :disabled="!imageUrl || fetchingUrl"
          @click="addFromUrl">
          <i :class="fetchingUrl ? 'fa-solid fa-spinner fa-spin' : 'fa-solid fa-download'" /> Lisa
        </button>
      </template>
    </Field>
  </div>
</template>

<script>
import api from '@/api'

export default {
  name: 'upload-field',
  emits: ['update:modelValue', 'update:colorImages'],
  props: {
    modelValue: { type: Array, default: () => [] },
    colorImages: { type: Array, default: () => [] },
    colorOptions: { type: Array, default: () => [] },
  },
  data() {
    return {
      uploading: false,
      fetchingUrl: false,
      imageUrl: '',
      pendingFiles: [],
      colorEditIndex: null,
      colorEditValue: '',
    }
  },
  computed: {
    images: {
      get() { return this.modelValue },
      set(v) { this.$emit('update:modelValue', v) }
    },
    allImages() {
      const uploaded = this.images.map(img => ({
        preview: this.resolveUrl(img),
        key: img,
        uploaded: true,
        color: this.getColorForImage(img),
      }))
      const pending = this.pendingFiles.map(pf => ({
        preview: pf.preview,
        key: pf.key,
        uploaded: false,
        color: null,
      }))
      return [...uploaded, ...pending]
    }
  },
  methods: {
    getColorForImage(img) {
      const ci = this.colorImages.find(c => c.image === img)
      return ci ? ci.color : null
    },
    toggleColorEdit(index) {
      if (this.colorEditIndex === index) {
        this.colorEditIndex = null
        return
      }
      const img = this.images[index]
      const existing = this.colorImages.find(c => c.image === img)
      this.colorEditValue = existing ? existing.color : ''
      this.colorEditIndex = index
    },
    saveColor(index) {
      const img = this.images[index]
      const next = this.colorImages.filter(c => c.image !== img)
      if (this.colorEditValue.trim()) {
        next.push({ color: this.colorEditValue.trim(), image: img })
      }
      this.$emit('update:colorImages', next)
      this.colorEditIndex = null
    },
    removeColor(index) {
      const img = this.images[index]
      this.$emit('update:colorImages', this.colorImages.filter(c => c.image !== img))
      this.colorEditIndex = null
    },
    resolveUrl(img) {
      if (!img) return ''
      if (img.startsWith('http')) return img
      const base = (import.meta.env.VITE_API_URL || '/api').replace(/\/$/, '')
      return base + img
    },
    addFiles(e) {
      const files = e.target.files
      if (!files?.length) return
      const maxSize = 10 * 1024 * 1024 // 10 MB
      const rejected = []
      for (const file of files) {
        if (file.size > maxSize) {
          rejected.push(file.name)
          continue
        }
        this.pendingFiles.push({
          file,
          preview: URL.createObjectURL(file),
          key: Math.random().toString(36).slice(2),
        })
      }
      if (rejected.length) {
        this.$error(`Liiga suured failid (max 10 MB): ${rejected.join(', ')}`)
      }
      e.target.value = ''
    },
    async addFromUrl() {
      const url = this.imageUrl.trim()
      if (!url) return
      this.fetchingUrl = true
      try {
        const { data } = await api.post('/upload/from-url', { url })
        this.images = [...this.images, data.url]
        this.imageUrl = ''
      } catch (err) {
        const msg = err.response?.data?.error || 'URL-ist pildi laadimine ebaõnnestus'
        this.$error(msg)
      } finally {
        this.fetchingUrl = false
      }
    },
    remove(index) {
      if (!confirm('Kas soovid pildi eemaldada?')) return
      const uploaded = this.images.length
      if (index < uploaded) {
        const removedImg = this.images[index]
        const next = [...this.images]
        next.splice(index, 1)
        this.images = next
        if (this.colorImages.some(c => c.image === removedImg)) {
          this.$emit('update:colorImages', this.colorImages.filter(c => c.image !== removedImg))
        }
      } else {
        const pf = this.pendingFiles.splice(index - uploaded, 1)[0]
        if (pf) URL.revokeObjectURL(pf.preview)
      }
    },
    setGridImage(index) {
      const uploaded = this.images.length
      if (index >= uploaded || index === 0) return
      const next = [...this.images]
      const [moved] = next.splice(index, 1)
      next.unshift(moved)
      this.images = next
    },
    async uploadPending() {
      if (!this.pendingFiles.length) return true
      this.uploading = true
      try {
        const formData = new FormData()
        for (const pf of this.pendingFiles) {
          formData.append('files', pf.file)
        }
        const { data } = await api.post('/upload/batch', formData, {
          headers: { 'Content-Type': 'multipart/form-data' }
        })
        this.images = [...this.images, ...data.urls]
        this.pendingFiles = []
        return true
      } catch (err) {
        const msg = err.response?.data?.error || 'Piltide üleslaadimine ebaõnnestus'
        this.$error(msg)
        return false
      } finally {
        this.uploading = false
      }
    }
  }
}
</script>

<style src="./upload.scss"></style>
